import React, { useMemo, useRef, useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Message } from '@/types';
import { formatRelativeTime, cleanMarkdownBold } from '@/lib/utils';
import { Code, Copy, CheckCheck, RefreshCw, Bot, User, Lock } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Button } from '@/components/ui/button';
import CodeHighlighter from '@/components/code/SyntaxHighlighter';
import LinkPreview from '@/components/common/LinkPreview';
import TypingIndicator from './TypingIndicator';
import AssistantThinkingBubble from './AssistantThinkingBubble';
import { useKnowledgeBases } from '@/hooks/useKnowledgeBases';
import { useProfile } from '@/hooks/useProfile';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

function extractCodeBlock(content: string): { code: string; language: string } | null {
  const codeBlockRegex = /```([a-zA-Z0-9]*)\n([\s\S]*?)```/m;
  const match = content.match(codeBlockRegex);
  if (!match) {
    return null;
  }

  return {
    language: match[1] || 'plaintext',
    code: match[2] || '',
  };
}

function stripMarkdownCodeFencing(raw: string): string {
  const codeBlockRegex = /^```(?:\w+)?\n([\s\S]*?)\n```$/;
  const match = raw.match(codeBlockRegex);
  return match ? match[1] : raw;
}

interface SimpleChatMessageProps {
  message: Message;
  hideCodeBlockIfAttachment?: boolean;
  onRegenerate?: (messageId: string) => void;
}

const SimpleChatMessage: React.FC<SimpleChatMessageProps> = React.memo(({ message, hideCodeBlockIfAttachment, onRegenerate }) => {
  const isAi = message.role === 'assistant';
  const [copied, setCopied] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const { knowledgeBases } = useKnowledgeBases();
  const { profile } = useProfile();
  const { toast } = useToast();

  const displayContent = useMemo(() => {
    if (!message.content || message.content.trim() === '...') {
      return message.content;
    }

    const kbMatch = message.content.match(/^\/kb\s+([^\s]+)(\s+.+)?/);
    if (!kbMatch) {
      return message.content;
    }

    const matched = knowledgeBases.find((kb) => kb.id === kbMatch[1]);
    if (!matched) {
      return message.content;
    }

    return `/kb ${matched.name}${kbMatch[2] || ''}`;
  }, [knowledgeBases, message.content]);

  const handleCopy = () => {
    if (!contentRef.current) {
      return;
    }

    const html = contentRef.current.innerHTML;
    const plainText = contentRef.current.innerText;

    try {
      const blobHtml = new Blob([html], { type: 'text/html' });
      const blobText = new Blob([plainText], { type: 'text/plain' });
      const clipboardItem = new ClipboardItem({
        'text/html': blobHtml,
        'text/plain': blobText,
      });

      navigator.clipboard.write([clipboardItem]).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    } catch (error) {
      console.error('Failed to copy rich text, falling back to plain text.', error);
      navigator.clipboard.writeText(plainText).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const handleSeal = async () => {
    if (!message.content || message.content.trim() === '...') {
      return;
    }

    if (localStorage.getItem('trust_attestation_enabled') === 'true') {
      const ok = window.confirm('Do you attest this output is trusted and want to seal it?');
      if (!ok) {
        return;
      }
    }

    const ts = new Date(message.timestamp || Date.now()).toISOString();
    const model = message.model || 'unknown';
    const md = [
      '# EHCO Sealed Reflection',
      '',
      `- Role: ${message.role}`,
      `- Model: ${model}`,
      `- Timestamp: ${ts}`,
      '',
      '## Content',
      message.content,
      '',
      '---',
      'Sealed locally. If signed in, it is also saved to your private EHCO memory.',
    ].join('\n');

    const nameSafe = model.toString().replace(/[^\w.-]+/g, '_');
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `EHCO_${nameSafe}_${ts}.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);

    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    if (!userId) {
      return;
    }

    const meta = {
      role: message.role,
      model,
      timestamp: ts,
      trust_enabled: localStorage.getItem('trust_mode_enabled') === 'true',
      chat_id: message.chatId || null,
      commands: message.commands || null,
      knowledge_base_id: message.knowledgeBaseId || null,
    };

    const { error } = await (supabase as any)
      .from('sealed_items')
      .insert({
        user_id: userId,
        chat_id: message.chatId || null,
        message_id: message.id,
        content: message.content,
        meta,
      });

    if (error) {
      console.error('[Seal] Failed to persist sealed item:', error);
      toast({
        title: 'Local seal saved',
        description: 'Could not sync to EHCO memory. Please try again later.',
        variant: 'destructive',
      });
    }
  };

  const codeAttachments = message.attachments?.filter((att) => att.type === 'code') || [];
  const imageAttachments = message.attachments?.filter((att) => att.type === 'image') || [];
  const canvasAttachments = message.attachments?.filter((att) => att.type === 'canvas') || [];
  const fileAttachments = message.attachments?.filter((att) => att.type === 'file') || [];
  const audioAttachments = message.attachments?.filter((att) => att.type === 'audio') || [];
  const codeBlock = !codeAttachments.length && displayContent ? extractCodeBlock(displayContent) : null;
  const thinking = isAi ? message.metadata?.thinking : undefined;
  const generation = isAi ? message.metadata?.generation : undefined;
  const isStopped = generation?.status === 'stopped';
  const isTyping = isAi && !isStopped && (!displayContent || displayContent.trim() === '...');
  const hasThinkingBubble = Boolean(thinking?.enabled && (thinking.status === 'streaming' || (thinking.providerExposed && thinking.text?.trim())));

  return (
    <div
      className={`py-4 px-2 ${isAi ? 'bg-muted/30' : ''}`}
      data-testid="chat-message"
      data-message-id={message.id}
    >
      <div className="mx-0 flex max-w-[95%] justify-start gap-3 md:max-w-[85%]">
        <Avatar className="h-8 w-8 flex-shrink-0">
          {isAi ? (
            <AvatarFallback>
              <Bot className="h-full w-full p-1 text-primary" />
            </AvatarFallback>
          ) : profile?.avatar ? (
            <AvatarImage src={profile.avatar} />
          ) : (
            <AvatarFallback>
              <User className="h-full w-full p-1 text-muted-foreground" />
            </AvatarFallback>
          )}
        </Avatar>

        <div className="flex-1 space-y-2 text-left">
          <div className="flex items-center gap-2">
            <span className="font-semibold">{isAi ? 'AI Assistant' : 'You'}</span>
            <span className="text-xs text-muted-foreground">
              {message.timestamp ? formatRelativeTime(message.timestamp) : 'unknown'}
            </span>
            {isStopped ? (
              <span className="rounded-full border border-amber-300/70 bg-amber-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-900 dark:border-amber-700/70 dark:bg-amber-900/30 dark:text-amber-200">
                Stopped
              </span>
            ) : null}
            {isAi && displayContent && displayContent.trim() !== '...' ? (

              <div className="ml-auto flex items-center">
                {onRegenerate ? (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => onRegenerate(message.id)}
                    title="Regenerate response"
                  >
                    <RefreshCw size={14} />
                  </Button>
                ) : null}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handleSeal}
                  title="Seal to memory"
                >
                  <Lock size={14} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handleCopy}
                  title="Copy message"
                >
                  {copied ? <CheckCheck size={14} className="text-white" /> : <Copy size={14} />}
                </Button>
              </div>
            ) : null}
          </div>

          <div ref={contentRef}>
            {thinking ? <AssistantThinkingBubble thinking={thinking} /> : null}

            {isTyping ? (
              hasThinkingBubble ? null : <TypingIndicator />
            ) : (
              <>
                {codeAttachments.length > 0 ? (
                  codeAttachments.map((attachment) => {
                    const language = attachment.name?.split('.').pop() || 'plaintext';
                    return (
                      <div key={attachment.id} className="mb-3">
                        <div className="mb-1 flex items-center gap-2">
                          <Code size={16} />
                          <span className="font-medium">{attachment.name || 'Code snippet'}</span>
                        </div>
                        <CodeHighlighter code={stripMarkdownCodeFencing(attachment.content)} language={language} />
                      </div>
                    );
                  })
                ) : codeBlock && !hideCodeBlockIfAttachment ? (
                  <div className="mb-3">
                    <div className="mb-1 flex items-center gap-2">
                      <Code size={16} />
                      <span className="font-medium">{codeBlock.language || 'Code snippet'}</span>
                    </div>
                    <CodeHighlighter code={stripMarkdownCodeFencing(codeBlock.code)} language={codeBlock.language} />
                  </div>
                ) : displayContent ? (
                  <div className="markdown-content whitespace-pre-wrap break-words text-sm text-foreground/90">
                    <ReactMarkdown
                      components={{
                        a: ({ ...props }) => <LinkPreview url={props.href || ''} />,
                        p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
                      }}
                      remarkPlugins={[remarkGfm]}
                    >
                      {cleanMarkdownBold(displayContent)}
                    </ReactMarkdown>
                  </div>
                ) : isStopped ? (
                  <div className="text-sm italic text-muted-foreground">Generation stopped.</div>
                ) : null}

                {imageAttachments.length > 0 ? (
                  <div className="mt-2 space-y-3">
                    {imageAttachments.map((attachment) => (
                      <div key={attachment.id} className="overflow-hidden rounded-md border">
                        <img
                          src={attachment.content}
                          alt={attachment.name || 'Attached image'}
                          className="max-h-96 w-full object-contain"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </div>
                    ))}
                  </div>
                ) : null}

                {canvasAttachments.length > 0 ? (
                  <div className="mt-2 space-y-3">
                    {canvasAttachments.map((attachment) => (
                      <div key={attachment.id} className="overflow-hidden rounded-md border">
                        <img
                          src={attachment.content}
                          alt="Drawing"
                          className="max-h-96 w-full object-contain"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      </div>
                    ))}
                  </div>
                ) : null}

                {fileAttachments.map((attachment) => (
                  <div key={attachment.id} className="flex items-center gap-2 rounded-md border p-3">
                    <span className="font-medium">{attachment.name || 'File'}</span>
                    <div className="text-xs text-muted-foreground">
                      {attachment.content.length > 100 ? `${attachment.content.substring(0, 100)}...` : attachment.content}
                    </div>
                  </div>
                ))}

                {audioAttachments.length > 0 ? (
                  <div className="mt-2 space-y-3">
                    {audioAttachments.map((attachment) => (
                      <div key={attachment.id} className="rounded-md border p-3">
                        <div className="mb-2 text-sm font-medium">{attachment.name || 'Audio clip'}</div>
                        {attachment.content.startsWith('data:audio/') ? (
                          <audio controls className="w-full">
                            <source src={attachment.content} />
                          </audio>
                        ) : (
                          <div className="text-xs text-muted-foreground">Audio attachment ready for multimodal models.</div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

SimpleChatMessage.displayName = 'SimpleChatMessage';

export default SimpleChatMessage;
